import { Form, FormInstance, message, ModalProps } from 'antd';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { APIClient } from '../../api/useAPIClient';
import useAPIMutation from '../../api/useAPIMutation';
import { RequestJobPostingCollection, RequestJobPostingDetail } from '../../types';
import CancelRequestJobPostingModal from './CancelRequestJobPostingModal';

export type RequestJobPostingActions = 'CANCELED';

interface RequestJobPostingActionsProps {
  requestJobPosting: RequestJobPostingCollection | RequestJobPostingDetail;
  action?: RequestJobPostingActions;
  open?: boolean;
  onOk?: () => void;
  onCancel?: () => void;
}

interface MutationsParams {
  requestJobPosting: RequestJobPostingCollection | RequestJobPostingDetail;
}

export interface JobPostingActionModal extends ModalProps {
  requestJobPosting: RequestJobPostingCollection | RequestJobPostingDetail;
  form?: FormInstance;
}

const RequestJobPostingActionsModal: React.FC<RequestJobPostingActionsProps> = ({
  action,
  requestJobPosting,
  open = false,
  onOk,
  onCancel,
}) => {
  const { t } = useTranslation();

  const [form] = Form.useForm();

  const queryClient = useQueryClient();

  const handleRequest = async (data: MutationsParams, api: APIClient) => {
    switch (action) {
      case 'CANCELED':
        return api.post(`request-job-postings/${data.requestJobPosting.id}/canceled/`, {
          comment: form.getFieldValue('comment') || '',
        });
    }
  };

  const mutation = useAPIMutation(async (data: MutationsParams, api) => handleRequest(data, api), {
    onSuccess: () => {
      switch (action) {
        case 'CANCELED':
          message.success(t('Requisição de vaga cancelada com sucesso!'));
          break;
      }

      form.resetFields();

      if (onOk) {
        onOk();
      }
    },
    onSettled: () => {
      queryClient.resetQueries('/request-job-postings/');
    },
    onError: (e, variables, context) => {
      return queryClient.defaultMutationOptions().onError?.(e, variables, context);
    },
  });

  const handleSubmit = async () => {
    mutation.mutate({ requestJobPosting });
  };

  return (
    <>
      <CancelRequestJobPostingModal
        title={
          <>
            <Trans>Cancelar requisição</Trans> <strong>{requestJobPosting.name}</strong>
          </>
        }
        requestJobPosting={requestJobPosting}
        open={open && action === 'CANCELED'}
        onOk={handleSubmit}
        onCancel={onCancel}
        confirmLoading={mutation.isLoading}
        form={form}
        destroyOnClose
      />
    </>
  );
};

export default RequestJobPostingActionsModal;
